import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, imgUrl } from "../lib/api";
import { ShoppingCart } from "lucide-react";

const STATUS_STYLE = {
  DRAFT: "bg-zinc-100 text-zinc-700",
  SENT: "bg-amber-100 text-amber-800",
  RECEIVED: "bg-emerald-100 text-emerald-800",
  CANCELLED: "bg-red-100 text-red-700",
};

export default function POsGallery() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/purchase-orders").then((r) => setItems(r.data)).finally(() => setLoading(false));
  }, []);

  return (
    <div className="p-10 max-w-6xl mx-auto">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-zinc-400">Production</p>
          <h1 className="font-heading text-4xl font-bold mt-1 tracking-tight" data-testid="pos-title">Purchase Orders</h1>
          <p className="text-zinc-500 mt-2 text-sm max-w-xl">Issue formal POs to suppliers for one or more styles from your collections. Track quantities, unit costs and grand total in one place.</p>
        </div>
      </div>

      {loading ? (
        <div className="mt-10 text-sm text-zinc-400">Loading…</div>
      ) : items.length === 0 ? (
        <div className="mt-10 border-2 border-dashed border-zinc-200 rounded-lg p-16 text-center text-zinc-400" data-testid="pos-empty-state">
          <ShoppingCart size={32} className="mx-auto mb-4 text-zinc-300" />
          <p className="text-sm">No purchase orders yet.</p>
          <p className="text-xs mt-2">Open a collection, select the styles you want to order and click <span className="font-medium text-zinc-600">Create Purchase Order</span>.</p>
          <Link to="/collections" className="mt-4 inline-block bg-zinc-950 text-white px-6 py-2.5 rounded-md text-sm font-medium hover:bg-zinc-800 transition-colors">Browse collections</Link>
        </div>
      ) : (
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((po) => (
            <Link key={po.id} to={`/purchase-orders/${po.id}`} data-testid={`po-card-${po.id}`}
              className="group border border-zinc-200 bg-white rounded-lg overflow-hidden hover:border-zinc-400 transition-colors">
              {po.cover_image ? (
                <img src={imgUrl(po.cover_image)} alt={po.po_number} className="w-full h-40 object-cover group-hover:scale-[1.02] transition-transform" />
              ) : (
                <div className="w-full h-40 bg-zinc-100 flex items-center justify-center text-zinc-300"><ShoppingCart size={32} /></div>
              )}
              <div className="p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">{po.po_number}</p>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${STATUS_STYLE[po.status] || STATUS_STYLE.DRAFT}`}>{po.status}</span>
                </div>
                <p className="text-sm font-medium truncate">{po.supplier_name || "(no supplier)"}</p>
                <div className="grid grid-cols-3 gap-2 pt-2 border-t border-zinc-100">
                  <div>
                    <p className="text-[10px] uppercase text-zinc-400">Styles</p>
                    <p className="text-sm font-medium">{po.items_count}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase text-zinc-400">Units</p>
                    <p className="text-sm font-medium">{po.total_units}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase text-zinc-400">Total</p>
                    <p className="text-sm font-medium">{po.currency} {po.grand_total.toFixed(2)}</p>
                  </div>
                </div>
                <p className="text-[11px] text-zinc-400">Issued {po.issue_date}</p>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
